import React from "react";
import { Rows2, Rows3 } from "lucide-react";
import useSheetDensity from "../hooks/useSheetDensity";

/**
 * The roomy/compact row toggle, in one place.
 *
 * Used twice with different scopes (see hooks/useSheetDensity.js): SheetFooter
 * drives the nine full-page sheets, and the vehicle modal's table tabs drive
 * their own, independent setting.
 *
 * THE ID IS OPT-IN, AND DELIBERATELY SO. The Sales tour (useTabJoyride.js:598)
 * spotlights #sheet-density-toggle, so SheetFooter passes that id and must keep
 * passing it. The modal tabs pass NO id, because FormTab (AddEditCommitted.jsx)
 * hides inactive tabs with `display: none` rather than unmounting them — so all
 * three tables are in the DOM at once, and any id hardcoded in here would
 * appear three times over. Nothing targets these three, so the honest answer is
 * no id at all. If one ever needs a tour step, give it a per-tab id, not a
 * shared one.
 *
 * The label is a prop for a related reason: on a sheet this sits in a labelled
 * row of controls, but in a modal tab it stands alone in a button bar where a
 * bare icon has nothing next to it to explain what it does.
 */
export default function DensityToggle({ id, scope = "sheet", showLabel = false }) {
    const [density, toggleDensity] = useSheetDensity(scope);
    const isCompact = density === "compact";

    return (
        <button
            id={id}
            type="button"
            className="btn-t btn-t-secondary sheet-density-btn"
            onClick={toggleDensity}
            aria-pressed={isCompact}
            title={isCompact ? "Switch to roomier rows" : "Switch to compact rows"}
        >
            {isCompact ? <Rows3 size={16} /> : <Rows2 size={16} />}
            {showLabel && <span>{isCompact ? "Compact" : "Roomy"}</span>}
        </button>
    );
}
